"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { PageHero } from "@/components/sections/page-hero";
import { PrivacyPolicyHeroIllustration } from "@/components/illustrations/privacy-policy-hero";
import { Container } from "@/components/layout/container";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

type TermsSection = {
  heading: string;
  body: string[];
};

const SECTIONS: TermsSection[] = [
  {
    heading: "Introduction",
    body: [
      "These Terms and Conditions govern your use of the Exec9 website and any services we provide. By accessing our website or engaging our services, you agree to be bound by these terms. If you do not agree, please do not use our website or services.",
    ],
  },
  {
    heading: "Who We Are",
    body: [
      "Exec9 is a service-based software development company located in Lahore, Pakistan. We specialize in custom software solutions, web and mobile application development, AI and blockchain development, UI/UX design, and IT consulting services.",
    ],
  },
  {
    heading: "Use of Our Website",
    body: [
      "You may use this website only for lawful purposes",
      "You must not attempt to gain unauthorized access to any part of the website or its systems",
      "You must not use the website to distribute malicious code, spam, or misleading content",
      "You must not copy, scrape, or reproduce website content for commercial use without our written permission",
    ],
  },
  {
    heading: "Our Services",
    body: [
      "The scope, deliverables, timelines, and fees for any project are defined in a separate proposal, statement of work, or service agreement agreed with each client. Where such an agreement conflicts with these Terms, the signed agreement takes precedence.",
    ],
  },
  {
    heading: "Payments and Fees",
    body: [
      "Fees, payment schedules, and milestones are set out in the applicable project agreement",
      "Invoices are payable within the period stated on the invoice",
      "Late or missed payments may result in work being paused until the account is settled",
      "All fees are exclusive of applicable taxes unless stated otherwise",
    ],
  },
  {
    heading: "Intellectual Property",
    body: [
      "Unless agreed otherwise in writing, ownership of the custom code and deliverables we build for you transfers to you once full payment has been received. Exec9 retains ownership of its pre-existing tools, libraries, and know-how, and all content on this website, including text, graphics, logos, and design, remains the property of Exec9.",
    ],
  },
  {
    heading: "Confidentiality",
    body: [
      "We treat all project information, business data, and materials you share with us as confidential and will not disclose them to third parties except as required to deliver the services or by law. Mutual non-disclosure agreements are available on request.",
    ],
  },
  {
    heading: "Client Responsibilities",
    body: [
      "Provide accurate requirements, feedback, and approvals in a timely manner",
      "Supply any content, credentials, or third-party access needed for the project",
      "Ensure that materials you provide do not infringe the rights of others",
    ],
  },
  {
    heading: "Warranties and Disclaimers",
    body: [
      "Our website and its content are provided on an \"as is\" basis. While we work to keep information accurate and up to date, we make no warranties about its completeness or reliability. Any warranties for delivered software are limited to those stated in the applicable project agreement.",
    ],
  },
  {
    heading: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Exec9 is not liable for any indirect, incidental, or consequential damages, including loss of profits or data, arising from your use of our website or services. Our total liability for any claim is limited to the amount paid to us for the services giving rise to that claim.",
    ],
  },
  {
    heading: "Third-Party Links and Services",
    body: [
      "Our website and deliverables may link to or rely on third-party websites, platforms, or services. We are not responsible for their content, availability, or practices, and your use of them is subject to their own terms.",
    ],
  },
  {
    heading: "Termination",
    body: [
      "Either party may terminate a project in line with the terms of the applicable project agreement. We may also restrict access to our website if these Terms are breached. Fees for work completed up to the date of termination remain payable.",
    ],
  },
  {
    heading: "Governing Law",
    body: [
      "These Terms are governed by the laws of Pakistan. Any disputes arising from them will be subject to the exclusive jurisdiction of the courts of Lahore, Pakistan.",
    ],
  },
  {
    heading: "Changes to These Terms",
    body: [
      "Exec9 may update these Terms periodically. The latest version will always be posted on this page, and continued use of our website or services after changes means you accept the updated Terms.",
    ],
  },
];

/** Static legal copy stays in real DOM text (server-rendered by the parent page component) so
 * it's indexable and readable with JS disabled — this component only adds a scroll-driven
 * reveal (GSAP) on top of content that's already present in the markup. */
export function TermsConditionsJourney() {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const cards = el.querySelectorAll<HTMLElement>("[data-policy-card]");

    if (prefersReducedMotion) {
      gsap.set(cards, { opacity: 1, y: 0, filter: "blur(0px)" });
      return;
    }

    const ctx = gsap.context(() => {
      cards.forEach((card, i) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 32, filter: "blur(6px)" },
          {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
            duration: 0.7,
            ease: "power3.out",
            scrollTrigger: {
              trigger: card,
              start: "top 88%",
              toggleActions: "play none none reverse",
            },
          }
        );

        const line = card.querySelector<HTMLElement>("[data-policy-rule]");
        if (line) {
          gsap.fromTo(
            line,
            { scaleX: 0 },
            {
              scaleX: 1,
              duration: 0.6,
              ease: "power2.out",
              delay: i * 0.02,
              scrollTrigger: {
                trigger: card,
                start: "top 88%",
                toggleActions: "play none none reverse",
              },
            }
          );
        }
      });
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <>
      <PageHero
        id="terms-conditions-hero"
        eyebrow="Legal"
        lines={["Terms & Conditions"]}
        description="The terms that govern your use of the Exec9 website and the services we provide."
        accentColor="#a374e8"
        seed={13}
        illustration={PrivacyPolicyHeroIllustration}
        illustrationLabel="Illustration representing legal terms and agreements"
      />

      <section className="relative py-10 sm:py-16 lg:py-24" aria-labelledby="terms-conditions-hero-heading">
        <Container>
          <div ref={listRef} className="flex max-w-3xl flex-col gap-10">
            {SECTIONS.map((section) => (
              <article
                key={section.heading}
                data-policy-card
                className="relative border-l border-[var(--color-border)] pl-6 will-change-transform"
              >
                <span
                  data-policy-rule
                  aria-hidden
                  className="absolute -left-px top-0 h-full w-px origin-top bg-gradient-to-b from-[var(--color-primary)] to-transparent"
                />
                <h2 className="font-display text-xl font-semibold text-[var(--color-text-primary)] sm:text-2xl">
                  {section.heading}
                </h2>
                {section.body.length > 1 ? (
                  <ul className="mt-3 space-y-2 text-[var(--color-text-secondary)]">
                    {section.body.map((line) => (
                      <li key={line} className="flex gap-2 leading-relaxed">
                        <span aria-hidden className="mt-2.5 h-1 w-1 shrink-0 rounded-full bg-[var(--color-primary-light)]" />
                        {line}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-3 leading-relaxed text-[var(--color-text-secondary)]">
                    {section.body[0]}
                  </p>
                )}
              </article>
            ))}

            <article data-policy-card className="relative border-l border-[var(--color-border)] pl-6 will-change-transform">
              <span
                data-policy-rule
                aria-hidden
                className="absolute -left-px top-0 h-full w-px origin-top bg-gradient-to-b from-[var(--color-primary)] to-transparent"
              />
              <h2 className="font-display text-xl font-semibold text-[var(--color-text-primary)] sm:text-2xl">
                Contact Us
              </h2>
              <p className="mt-3 leading-relaxed text-[var(--color-text-secondary)]">
                If you have any questions about these Terms and Conditions, feel free to reach out at{" "}
                <Link
                  href="mailto:info@exec9.com"
                  className="text-[var(--color-primary-text)] underline underline-offset-4 hover:text-[var(--color-primary)]"
                >
                  info@exec9.com
                </Link>
                . You can also review our{" "}
                <Link
                  href="/privacy-policy"
                  className="text-[var(--color-primary-text)] underline underline-offset-4 hover:text-[var(--color-primary)]"
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </article>
          </div>
        </Container>
      </section>
    </>
  );
}
